/**
 * `file` — file chips with a preview affordance; **read-only through the cell**.
 *
 * *"Files can only be added through the `add_file_to_column` mutation. The JSON column value
 * mutations only support the `clear_all` operation."* `[09§A.7 file]` — so there is no cell
 * editor that writes a value. The popover is a gallery: one row per entry, an image thumbnail
 * for an `ASSET` whose `isImage` is `"true"`, the link itself for a `LINK`, and an upload row
 * that raises the board's own uploader.
 */
import {
  fileEntryUrl,
  firstImageEntry,
  getColumnType,
  isImageEntry,
  type FileEntry,
  type FileSettings,
  type FileValue,
} from '@elizmonday/shared';
import { CellPopover } from '../primitives';
import { requestCellAction } from '../cell-action';
import { defineColumnUi } from '../types';
import { FileSettingsDialog } from './SettingsDialog';

const shared = getColumnType('file');

export const FILE_GALLERY_WIDTH = 320;

export const fileColumnUi = defineColumnUi<FileValue, FileSettings>({
  type: 'file',
  icon: 'upload',
  gallery: { group: 'advanced', keywords: ['file', 'files', 'attachment', 'upload', 'image'] },
  /** P-022: `4098059368` `Item Locations` (`width: null`) renders **140** px in monday. */
  defaultWidth: 140,
  align: 'center',
  groupable: false,
  /** The editor is the gallery, not a value editor, so it opens on a read-only cell too. */
  opensWhenReadOnly: true,

  Cell: ({ value }) => {
    const files = value?.files ?? [];
    if (files.length === 0) return <span className="ez-cell__empty" aria-hidden="true" />;
    const first = files[0] as FileEntry;
    return (
      <span className="ez-files" title={files.map((entry) => entry.name).join(', ')}>
        {isImageEntry(first) ? (
          <img className="ez-files__thumb" src={fileEntryUrl(first) ?? ''} alt="" width={20} height={20} />
        ) : (
          <span className="ez-files__glyph" aria-hidden="true">
            📎
          </span>
        )}
        <span className="ez-files__name">{first.name}</span>
        {files.length > 1 ? <span className="ez-chip ez-chip--more">{`+${files.length - 1}`}</span> : null}
      </span>
    );
  },

  Editor: ({ value, column, item, onCommit, onCancel }) => {
    const files = value?.files ?? [];
    return (
      <CellPopover label="Files" width={FILE_GALLERY_WIDTH} onDismiss={onCancel}>
        <ul className="ez-file-list">
          {files.map((entry, index) => {
            const url = fileEntryUrl(entry);
            return (
              <li key={`${entry.name}-${String(index)}`} className="ez-file-list__row">
                {isImageEntry(entry) ? (
                  <img className="ez-file-list__thumb" src={url ?? ''} alt="" width={32} height={32} />
                ) : (
                  <span className="ez-file-list__glyph" aria-hidden="true">
                    {entry.fileType === 'LINK' ? '🔗' : '📄'}
                  </span>
                )}
                <a
                  className="ez-file-list__name"
                  href={url ?? '#'}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid={`file-link-${String(index)}`}
                >
                  {entry.name}
                </a>
              </li>
            );
          })}
          {files.length === 0 ? <li className="ez-option-list__empty">No files yet</li> : null}
        </ul>
        {/*
          * The upload row this gallery's header has always described. Adding a file is
          * `add_file_to_column` — the uploader, not a cell write — so the row raises a
          * `file-upload` request and the board screen (`table/cell-actions.ts`) posts it to
          * the upload route (P-128). The input itself is hidden: the row is the affordance.
          */}
        <label className="ez-picker__row ez-picker__row--centered" data-testid="file-add">
          Add file
          <input
            type="file"
            hidden
            data-testid="file-upload-input"
            onChange={(event) => {
              const chosen = event.target.files?.[0];
              // Re-opening the picker on the same file must raise the request again.
              event.target.value = '';
              if (chosen) requestCellAction('file-upload', column.id, String(item.id), { file: chosen });
            }}
          />
        </label>
        {/*
          * `clear_all` is the one write monday's JSON path accepts, and it is the only cell
          * write offered here.
          */}
        <button
          type="button"
          className="ez-picker__row ez-picker__row--centered"
          data-testid="file-clear-all"
          disabled={files.length === 0}
          onClick={() => onCommit(null)}
        >
          Remove all files
        </button>
      </CellPopover>
    );
  },

  SettingsDialog: FileSettingsDialog,

  fromClipboard() {
    // A file cannot be pasted as text: monday refuses the write too `[09§A.7 file]`.
    throw new Error('the files column cannot be pasted into');
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf() {
    // A file has no bucket worth filtering by; the pane leaves the column out (B5 §5.1).
    return [];
  },

  /** A card's cover band: the **first image** asset, at our own `/files/<assetId>` URL. */
  coverImageOf(value) {
    const entry = firstImageEntry(value);
    return entry === null ? null : fileEntryUrl(entry);
  },
});
